import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, Select, TextInput } from "@opendate-app/setlist";
import CurrencyInput from "../../CurrencyInput/components/CurrencyInput";
import ConfirmTicketDrawer from "../../ConfirmTicketing/components/TicketDrawer";
import OpendateDistributionTermsModal from "../../ConfirmTicketing/components/OpendateDistributionTermsModal";
import { blankTicketType, dayOfShowPrice, dynamicPricingProblem, SALES_WINDOWS, WAIT_FOR_WINDOWS, withDayOfShowPrice } from "../scheduleBuilder";
import { fetchFeeQuote, formToTicket, namedTicketTypes, ticketToForm } from "../confirmAdapters";
import PresaleWindowsSection, { presaleWindowProblems } from "./PresaleWindowsSection";
import ShowDetailOffset, { offsetsWithinAYear } from "./ShowDetailOffset";
import { toastError } from "../../../shared/toastHelper";
import { priceOptionsValid } from "../../ConfirmTicketing/utils/priceOptions";

const blankDraft = () => {
  const { id: _id, price: _price, ...defaults } = blankTicketType();
  return { ...defaults, pricing: "paid", price: "" };
};

const WINDOW_KINDS = [
  { value: "default", label: "Public on-sale (each date)" },
  { value: "showDetails", label: "Based on show details" },
  { value: "after", label: "After another ticket" },
];

const WAIT_FOR_OPTIONS = [
  { value: SALES_WINDOWS.afterSellOut, label: "sells out" },
  { value: SALES_WINDOWS.afterSalesEnd, label: "stops selling" },
  { value: SALES_WINDOWS.afterEither, label: "sells out or stops selling" },
];

const windowKindOf = (draft) => {
  if (WAIT_FOR_WINDOWS.includes(draft.availability)) return "after";
  if (draft.availability === SALES_WINDOWS.showDetails) return "showDetails";
  return "default";
};

// Every rule the series enforces before a ticket can be saved. The shared
// drawer only knows the confirm's checks, so the series computes canSave here.
export const ticketProblems = (draft, { takenNames = [], seated = false, timedEntry = false, otherTicketNames = [], canUseDynamicPricing = false, allowFixedFees = false }) => {
  const isFree = draft.pricing === "free";
  const windows = draft.presaleWindows || [];
  const name = draft.name.trim();
  const windowKind = windowKindOf(draft);
  const minimum = Number(draft.minimumPerOrder);
  const maximum = Number(draft.maximumPerOrder);
  const windowProblems = presaleWindowProblems(windows, { ticketName: draft.name, takenNames, inventory: draft.inventory });
  const problems = {
    name: !name ? "missing" : takenNames.includes(name) || windows.some((window) => window.name.trim() === name) ? "Another ticket type already uses this name." : null,
    price: !isFree && draft.price === "" ? "missing" : null,
    limits: draft.maximumPerOrder === "" || draft.minimumPerOrder === "" || minimum > maximum ? "limits" : null,
    window:
      (windowKind === "showDetails" && !(draft.startActivity && (timedEntry || draft.endActivity))) ||
      (windowKind === "after" && !otherTicketNames.includes(draft.waitForName))
        ? "window"
        : null,
    scan:
      Boolean(draft.scanStartActivity) !== Boolean(draft.scanEndActivity) ||
      (draft.scanStartActivity && draft.scanStartActivity === draft.scanEndActivity)
        ? "Scanning has to open and close on different show details."
        : null,
    offsets: offsetsWithinAYear(draft.startOffsetMinutes, draft.endOffsetMinutes, draft.scanStartOffsetMinutes, draft.scanEndOffsetMinutes) ? null : "offsets",
    presaleWindows: Object.values(windowProblems).some(Boolean) ? "presaleWindows" : null,
    manifested: draft.manifested === false && windows.length > 0 ? "Pre-sale windows need a manifested ticket. Turn on Manifested Ticket or remove the windows." : null,
    // A template sells from its own quantity on every new event (EventSeries#create_ticket_type_template!).
    quantity: draft.shouldBeDefault && !seated && String(draft.inventory ?? "") === "" ? "A default ticket needs an available quantity." : null,
    fixedFee: allowFixedFees && !isFree && draft.hasFixedFees && draft.fixedFeeAmount === "" ? "fixedFee" : null,
    dynamicPricing: canUseDynamicPricing && !isFree ? dynamicPricingProblem(draft) : null,
  };
  return { problems, windowProblems };
};

const TicketTypeDrawer = ({
  open,
  initial,
  editing,
  takenNames = [],
  inventoryHint,
  seated = false,
  timedEntry = false,
  showActivityNames = [],
  otherTickets = [],
  manualTaxes = [],
  canUseDynamicPricing = false,
  dynamicPricingDefault = false,
  roundUpDefault = false,
  allowFixedFees = false,
  defaultCheckInPeriod = "all_time",
  distributionTermsAgreed = false,
  onAgreeToDistributionTerms,
  feeQuoteUrl,
  venueOwnershipId,
  csrfToken,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState(blankDraft);
  const [seeded, setSeeded] = useState(false);
  const [quote, setQuote] = useState({});
  const [termsOpen, setTermsOpen] = useState(false);
  const [agreeingToTerms, setAgreeingToTerms] = useState(false);
  const quoteTimer = useRef(null);

  useEffect(() => {
    if (open) {
      const next = initial
        ? { ...blankDraft(), ...initial, dynamicPricingEnabled: canUseDynamicPricing && Boolean(initial.dynamicPricingEnabled) }
        : { ...blankDraft(), dynamicPricingEnabled: canUseDynamicPricing && dynamicPricingDefault };
      setQuote({});
      setDraft(next);
      setSeeded(true);
      recalculate(ticketToForm(next, adapterOptions));
    } else {
      setSeeded(false);
    }
  }, [open, initial]);
  useEffect(() => () => clearTimeout(quoteTimer.current), []);

  const otherTicketNames = otherTickets.map((ticket) => ticket.name).filter(Boolean);
  const adapterOptions = { manualTaxes, allowFixedFees, defaultCheckInPeriod, editing: editing ?? Boolean(initial) };
  const form = { ...ticketToForm(draft, adapterOptions), ...quote };
  const { problems, windowProblems } = ticketProblems(draft, {
    takenNames, seated, timedEntry, otherTicketNames, canUseDynamicPricing, allowFixedFees,
  });
  const canSave = Object.values(problems).every((problem) => !problem) && priceOptionsValid(form);

  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const recalculate = (next) => {
    if (!feeQuoteUrl) return;
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(() => {
      fetchFeeQuote({ url: feeQuoteUrl, csrfToken, venueOwnershipId, kind: "ticket_type", form: next })
        .then(setQuote)
        .catch(() => toastError("Failed to calculate ticket costs"));
    }, 500);
  };

  const agreeToTerms = () => {
    setAgreeingToTerms(true);
    Promise.resolve(onAgreeToDistributionTerms())
      .then(() => {
        setTermsOpen(false);
        set("opendateDistribution", true);
      })
      .catch(() => toastError("Failed to accept terms. Please try again."))
      .finally(() => setAgreeingToTerms(false));
  };

  const activityOptions = showActivityNames.map((name) => ({ value: name, label: name }));
  const windows = draft.presaleWindows || [];
  const windowsUnsupported = seated || timedEntry;
  const windowKind = windowKindOf(draft);
  const isFree = draft.pricing === "free";
  const ownScanWindow = Boolean(draft.scanStartActivity || draft.scanEndActivity);
  const setWindowKind = (kind) =>
    setDraft((current) => ({
      ...current,
      availability: { default: null, showDetails: SALES_WINDOWS.showDetails, after: SALES_WINDOWS.afterEither }[kind],
      startActivity: kind === "showDetails" ? current.startActivity : "",
      endActivity: kind === "showDetails" ? current.endActivity : "",
      waitForName: kind === "after" ? current.waitForName : "",
    }));

  const renderSalesWindows = () => (
    <div className="ticket-drawer-sales" data-testid="series-ticket-sales-windows">
      <div className="ticket-drawer-sales-header">
        <span className="ticket-drawer-sales-title">Sales windows</span>
        <span className="ticket-drawer-sales-hint">Times from each date&apos;s show details</span>
      </div>
      <div className="form-group">
        <Select
          id="ticket-type-availability"
          label="When are tickets available?"
          options={WINDOW_KINDS.filter(
            (kind) => (kind.value !== "showDetails" || activityOptions.length > 0) && (kind.value !== "after" || otherTicketNames.length > 0),
          )}
          value={windowKind}
          onChange={(kind) => setWindowKind(kind || "default")}
        />
      </div>
      {windowKind === "default" && (
        <p className="ticket-drawer-muted-text">
          {timedEntry
            ? "On sale from each date's public on-sale until its entry time ends."
            : "On sale from each date's public on-sale until the show ends."}
        </p>
      )}
      {windowKind === "showDetails" && (
        <>
          <div className="ticket-drawer-field-row form-group">
            <div className="ticket-drawer-field">
              <Select
                label="Starts"
                placeholder="Select a show detail"
                options={activityOptions}
                value={draft.startActivity || null}
                onChange={(value) => setDraft((current) => ({ ...current, startActivity: value || "", startOffsetMinutes: 0 }))}
              />
            </div>
            {!timedEntry && (
              <div className="ticket-drawer-field">
                <Select
                  label="Ends"
                  placeholder="Select a show detail"
                  options={activityOptions}
                  value={draft.endActivity || null}
                  onChange={(value) => setDraft((current) => ({ ...current, endActivity: value || "", endOffsetMinutes: 0 }))}
                />
              </div>
            )}
          </div>
          {draft.startActivity && (
            <ShowDetailOffset label="Starts" anchorName={draft.startActivity} minutes={draft.startOffsetMinutes} onChange={(minutes) => set("startOffsetMinutes", minutes)} />
          )}
          {!timedEntry && draft.endActivity && (
            <ShowDetailOffset label="Ends" anchorName={draft.endActivity} minutes={draft.endOffsetMinutes} onChange={(minutes) => set("endOffsetMinutes", minutes)} />
          )}
          {timedEntry && <p className="ticket-drawer-muted-text">Each entry time stops selling when it ends.</p>}
        </>
      )}
      {windowKind === "after" && (
        <div className="ticket-drawer-field-row form-group">
          <div className="ticket-drawer-field">
            <Select
              id="ticket-type-wait-for-ticket-type"
              label="Starts after"
              placeholder="Select a ticket"
              options={otherTicketNames.map((name) => ({ value: name, label: name }))}
              value={draft.waitForName || null}
              onChange={(value) => set("waitForName", value || "")}
            />
          </div>
          <div className="ticket-drawer-field">
            <Select label="When it" options={WAIT_FOR_OPTIONS} value={draft.availability} onChange={(value) => set("availability", value)} />
          </div>
        </div>
      )}
      {!isFree && (
        <div className="form-group">
          <TextInput
            as={CurrencyInput}
            id="ticket-type-day-of-show-price"
            label="Day of Show price"
            hint="Starts at doors on each date, or when the show starts if a date has no doors."
            placeholder="Same as price"
            leadingText="$"
            maskOptions={{ prefix: "", integerLimit: null }}
            value={dayOfShowPrice(draft)}
            onChange={(event) => {
              const { value } = event.target;
              setDraft((current) => withDayOfShowPrice(current, value));
            }}
          />
        </div>
      )}
      {(!windowsUnsupported || windows.length > 0) && (
        <PresaleWindowsSection
          windows={windows}
          ticketName={draft.name}
          showActivityNames={showActivityNames}
          unsupported={windowsUnsupported}
          free={isFree}
          problems={windowProblems}
          onChange={(next) => set("presaleWindows", next)}
        />
      )}
      {problems.manifested && <p className="ticket-drawer-error-text">{problems.manifested}</p>}
    </div>
  );

  const renderEntryWindow = () =>
    !timedEntry && activityOptions.length > 0 ? (
      <>
        <div className="form-group">
          <Checkbox
            id="ticket-type-scan-window"
            label="Timed entry: only scan this ticket in between two show details"
            checked={ownScanWindow}
            onChange={(event) => {
              const { checked } = event.target;
              setDraft((current) => ({
                ...current,
                scanStartActivity: checked ? "Doors" : "",
                scanEndActivity: checked ? "Event End" : "",
              }));
            }}
          />
        </div>
        {ownScanWindow && (
          <>
            <div className="ticket-drawer-field-row form-group">
              <div className="ticket-drawer-field">
                <Select
                  label="Scanning opens"
                  placeholder="Select a show detail"
                  options={activityOptions}
                  value={draft.scanStartActivity || null}
                  onChange={(value) => setDraft((current) => ({ ...current, scanStartActivity: value || "", scanStartOffsetMinutes: 0 }))}
                />
              </div>
              <div className="ticket-drawer-field">
                <Select
                  label="Scanning closes"
                  placeholder="Select a show detail"
                  options={activityOptions}
                  value={draft.scanEndActivity || null}
                  onChange={(value) => setDraft((current) => ({ ...current, scanEndActivity: value || "", scanEndOffsetMinutes: 0 }))}
                />
              </div>
            </div>
            {draft.scanStartActivity && (
              <ShowDetailOffset label="Scanning opens" anchorName={draft.scanStartActivity} minutes={draft.scanStartOffsetMinutes} onChange={(minutes) => set("scanStartOffsetMinutes", minutes)} />
            )}
            {draft.scanEndActivity && (
              <ShowDetailOffset label="Scanning closes" anchorName={draft.scanEndActivity} minutes={draft.scanEndOffsetMinutes} onChange={(minutes) => set("scanEndOffsetMinutes", minutes)} />
            )}
            {problems.scan && <p className="ticket-drawer-error-text">{problems.scan}</p>}
          </>
        )}
      </>
    ) : null;

  // Mount the shared drawer only once this open's draft is in, so it never
  // starts from the previous ticket.
  if (!open || !seeded) return null;

  return (
    <>
      <ConfirmTicketDrawer
        ticketTypeForForm={form}
        confirm={{
          ticketing_arrangement: seated ? "Seated" : "General Admission",
          check_in_period: defaultCheckInPeriod,
          venue_ownership: { default_round_up_ticket_types: roundUpDefault, default_dynamic_pricing_ticket_types: dynamicPricingDefault },
        }}
        showActivities={[]}
        ticketTypes={namedTicketTypes(otherTickets)}
        team={{ package: { dynamic_pricing: canUseDynamicPricing } }}
        manualTaxes={manualTaxes}
        ticketTypeIsSeated={() => seated}
        canSave={canSave}
        closeOnEsc={!termsOpen}
        quantityHint={inventoryHint}
        canDistribute={!seated}
        nameError={problems.name && problems.name !== "missing" ? problems.name : undefined}
        quantityError={problems.quantity || undefined}
        renderSalesWindows={renderSalesWindows}
        generalAdmissionPriceOptions={false}
        renderEntryWindow={renderEntryWindow}
        onChange={(next) => setDraft((current) => formToTicket(next, current, adapterOptions))}
        onRecalculate={recalculate}
        onOpendateDistributionChange={(checked) => {
          if (checked && !distributionTermsAgreed) {
            setTermsOpen(true);
            return;
          }
          set("opendateDistribution", checked);
        }}
        onSave={() =>
          onSave({
            ...draft,
            endActivity: windowKind === "showDetails" && timedEntry ? draft.endActivity || "Event End" : draft.endActivity,
          })
        }
        onCancel={onCancel}
        onClose={onCancel}
      />
      <OpendateDistributionTermsModal
        open={termsOpen}
        agreeing={agreeingToTerms}
        onDecline={() => setTermsOpen(false)}
        onAgree={agreeToTerms}
      />
    </>
  );
};

TicketTypeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.object,
  editing: PropTypes.bool,
  takenNames: PropTypes.arrayOf(PropTypes.string),
  inventoryHint: PropTypes.string,
  seated: PropTypes.bool,
  timedEntry: PropTypes.bool,
  showActivityNames: PropTypes.arrayOf(PropTypes.string),
  otherTickets: PropTypes.arrayOf(PropTypes.object),
  manualTaxes: PropTypes.arrayOf(PropTypes.object),
  canUseDynamicPricing: PropTypes.bool,
  dynamicPricingDefault: PropTypes.bool,
  roundUpDefault: PropTypes.bool,
  allowFixedFees: PropTypes.bool,
  defaultCheckInPeriod: PropTypes.string,
  distributionTermsAgreed: PropTypes.bool,
  onAgreeToDistributionTerms: PropTypes.func,
  feeQuoteUrl: PropTypes.string,
  venueOwnershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  csrfToken: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default TicketTypeDrawer;
